import { Content, asText } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  SolutionsSliceDefault,
  SolutionsSliceDescriptionDetails,
} from '../../../prismicio-types';
import type * as prismic from '@prismicio/client';

enum SLICE_VARIATION {
  default = 'default',
  descriptionDetails = 'descriptionDetails',
}

/**
 * Props for `Solutions`.
 */
export type SolutionsProps = SliceComponentProps<Content.SolutionsSlice>;
export type DefaultVariation = prismic.SharedSlice<
  'solutions',
  SolutionsSliceDefault
>;
export type DescriptionDetailVariation = prismic.SharedSlice<
  'solutions',
  SolutionsSliceDescriptionDetails
>;

/**
 * Component for "Solutions" Slices.
 */
const Solutions = ({ slice }: SolutionsProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="px-5 md:px-16 py-28 max-w-screen-2xl mx-auto"
    >
      {slice.variation === SLICE_VARIATION.default && defaultVariation(slice)}
      {slice.variation === SLICE_VARIATION.descriptionDetails &&
        descriptionDetailVariation(slice)}
    </section>
  );
};

const defaultVariation = (slice: DefaultVariation) => {
  const {
    primary: {
      title,
      description,
      cards,
      button_link: buttonLink,
      button_label: buttonLabel,
      button_aria_label: ariaLabel,
    },
  } = slice;

  return (
    <div className="text-white flex flex-col gap-y-20">
      <div className="text-center w-full max-w-[48rem] mx-auto">
        <PrismicRichText field={title} />
        <PrismicRichText
          field={description}
          components={{
            paragraph: ({ children }) => (
              <p className="text-medium-normal mt-6">{children}</p>
            ),
          }}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-y-20">
        {cards.map(
          ({ icon, title, description, button_label, button_link }, index) => (
            <PrismicNextLink
              key={`card-${index}`}
              className="flex flex-col gap-y-6"
              field={button_link}
            >
              <PrismicNextImage field={icon} />
              <PrismicRichText field={title} />
              <PrismicRichText field={description} />
              {button_label && (
                <button
                  className="button-outline-white"
                  aria-label={`Learn more about ${asText(title)}`}
                >
                  <span>{button_label}</span>
                </button>
              )}
            </PrismicNextLink>
          ),
        )}
      </div>
      {buttonLabel && (
        <div className="flex justify-center">
          <PrismicNextLink
            field={buttonLink}
            className="button-bordered"
            aria-label={`${ariaLabel ?? 'Learn more about our solutions'}`}
          >
            <span>{buttonLabel}</span>
          </PrismicNextLink>
        </div>
      )}
    </div>
  );
};

const descriptionDetailVariation = (slice: DescriptionDetailVariation) => {
  const {
    primary: {
      title,
      description,
      background,
      cards,
      button_link: buttonLink,
      button_label: buttonLabel,
    },
  } = slice;

  return (
    <div className="text-white flex flex-col gap-8">
      <div className="w-full">
        <PrismicRichText field={title} />
        <PrismicRichText
          field={description}
          components={{
            paragraph: ({ children }) => (
              <p className="text-medium-normal mt-6">{children}</p>
            ),
          }}
        />
      </div>
      <div className="flex flex-col md:flex-row gap-8">
        <div className="flex-1">
          <div className="space-y-8">
            {cards.map(({ title, description }, index) => (
              <div
                key={`card-${index}`}
                className={`mt-4 px-6 ${index === 0 ? 'border-l-[1.5px] border-green-400' : ''}`}
              >
                <PrismicRichText field={title} />
                <div className="mt-6">
                  <PrismicRichText field={description} />
                </div>
              </div>
            ))}
            {buttonLabel && (
              <div className="flex justify-left pt-9 px-6">
                <PrismicNextLink field={buttonLink} className="button-bordered">
                  <span>{buttonLabel}</span>
                </PrismicNextLink>
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 justify-center items-center hidden sm:flex">
          <div className="overflow-hidden w-full h-full">
            <PrismicNextImage
              field={background}
              className="rounded-[20px] h-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Solutions;
